/**
 * EzhireDataBar — page toolbar parity with Marketing's
 * `<FreshnessBadge> + <SyncNowButton>` + DB popover pattern.
 *
 * Composes:
 *   - Freshness pill ("Data as of X min ago", with status colour)
 *   - Sync now button (clears the React Query cache for ezhire-* keys
 *     + re-tests the connector + refetches active queries)
 *   - Data-source popover (last test result, server identity, tables visible)
 *   - The standard <TimePeriodSelector> from @ezhire/ui
 *   - A Refresh button (kept for muscle memory; same effect as Sync now)
 *
 * Used by every Dispatch / Demand / Finance Close page in place of the
 * earlier raw <TimePeriodSelector> + Refresh combo. The freshness pill
 * shows the connector's last_tested_at; "X min ago" updates every 30s
 * via the connector status auto-refresh.
 */

import {
  Badge,
  Button,
  Freshness,
  Popover,
  PopoverContent,
  PopoverTrigger,
  TimePeriodSelector,
  cn,
  toast,
  type TimePeriodValue,
} from '@ezhire/ui';
import { useEzhireConnectorStatus, useEzhireQuery } from '../hooks/use-ezhire-query';
import { useQueryClient } from '@tanstack/react-query';
import { CheckCircle2, Database, Loader2, RefreshCw, XCircle } from 'lucide-react';
import { useState } from 'react';
import { describeError } from '@ezhire/types';

interface EzhireDataBarProps {
  period: TimePeriodValue;
  onPeriodChange: (next: TimePeriodValue) => void;
  /** Caller's local refresh handler — typically `usePeriod().refresh`. */
  onRefresh: () => void;
  className?: string;
}

interface ServerIdentityRow {
  who: string;
  host: string;
  version: string;
  db_tz: string;
  now_db: string;
}

interface TablesVisibleRow { visible_tables: number }

export function EzhireDataBar({ period, onPeriodChange, onRefresh, className }: EzhireDataBarProps) {
  const qc = useQueryClient();
  const status = useEzhireConnectorStatus();
  const [syncing, setSyncing] = useState(false);

  const ok = status.data?.ok === true;
  const checkedAt = status.data?.checkedAt;

  const handleSyncNow = async () => {
    setSyncing(true);
    try {
      // Invalidate the connector status + every ezhire query.
      await qc.invalidateQueries({ queryKey: ['ezhire-connector-status'] });
      await qc.invalidateQueries({ queryKey: ['ezhire'] });
      // Trigger the page-level refresh handler (bumps the period reducer).
      onRefresh();
      toast.success('Sync complete — refreshing data.');
    } catch (err) {
      const msg = describeError(err, 'Sync failed.');
      toast.error(msg);
    } finally {
      setSyncing(false);
    }
  };

  return (
    <div className={cn('flex flex-wrap items-center gap-3', className)}>
      <Freshness
        timestamp={checkedAt}
        staleAfterHours={2}
        // Live-data via direct query: results reflect the DR-clone state at
        // query-time, not a sync run. Treating as "Provisional" is honest —
        // the DR clone may lag the production primary by replication.
        provisional
      />
      <Button
        type="button"
        variant="secondary"
        size="sm"
        onClick={() => void handleSyncNow()}
        loading={syncing || status.isFetching}
      >
        {!syncing && !status.isFetching ? <RefreshCw className="h-3.5 w-3.5" /> : null}
        Sync now
      </Button>
      <DataSourcePopover ok={ok} statusMessage={status.data?.message} enabled={ok} />
      <TimePeriodSelector
        value={period}
        onChange={onPeriodChange}
        onRefresh={onRefresh}
      />
    </div>
  );
}

function DataSourcePopover({
  ok,
  statusMessage,
  enabled,
}: {
  ok: boolean;
  statusMessage: string | undefined;
  enabled: boolean;
}) {
  const identity = useEzhireQuery<ServerIdentityRow>('__server_identity__', {}, { enabled });
  const tables = useEzhireQuery<TablesVisibleRow>('__tables_visible_count__', {}, { enabled });

  const ident = identity.data?.data?.[0];
  const visible = tables.data?.data?.[0]?.visible_tables;

  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button
          type="button"
          variant="secondary"
          size="sm"
          aria-label="Data source details"
          title="Data source details"
        >
          <Database className="h-3.5 w-3.5" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[360px] p-0" align="end">
        <div className="border-b border-border p-4">
          <div className="flex items-center gap-3">
            <div className="rounded-md border border-border bg-bg-elevated p-2">
              <Database className="h-4 w-4 text-accent-500" aria-hidden />
            </div>
            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-2">
                <span className="text-h3 text-text-primary truncate">eZhire Operational DB</span>
                {ok ? (
                  <span className="inline-flex items-center gap-1 rounded-full border border-green-200 bg-green-50 px-2 py-0.5 text-[11px] font-medium text-green-700 dark:border-green-900 dark:bg-green-950">
                    <CheckCircle2 className="h-3 w-3" /> green
                  </span>
                ) : (
                  <span className="inline-flex items-center gap-1 rounded-full border border-red-200 bg-red-50 px-2 py-0.5 text-[11px] font-medium text-red-700 dark:border-red-900 dark:bg-red-950">
                    <XCircle className="h-3 w-3" /> red
                  </span>
                )}
              </div>
              <div className="text-caption text-text-muted">MySQL · myproject schema · DRClone</div>
            </div>
          </div>
        </div>
        <div className="space-y-3 p-4 text-caption">
          {ok ? (
            <>
              <Row label="DB user" value={ident?.who ?? <Loader2 className="h-3 w-3 animate-spin" />} />
              <Row label="DB host" value={ident?.host ?? '—'} />
              <Row label="MySQL version" value={ident?.version ?? '—'} />
              <Row label="Timezone" value={ident?.db_tz ?? '—'} />
              <Row label="DB clock" value={ident?.now_db ?? '—'} />
              <Row
                label="Tables visible"
                value={
                  visible != null
                    ? `${visible.toLocaleString('en-US')} tables / views`
                    : (
                        <Loader2 className="h-3 w-3 animate-spin" aria-hidden />
                      )
                }
              />
            </>
          ) : (
            <p className="text-text-muted">
              Connector is in <span className="font-medium text-red-700">red</span> status.
              {statusMessage ? <span className="block mt-2">{statusMessage}</span> : null}
            </p>
          )}
          <div className="pt-2 border-t border-border">
            <p className="text-text-muted">
              <Badge variant="outline" className="mr-2">Pattern B</Badge>
              Direct read-only query via the bastion.
              No ETL — production-ingest path comes after the demo.
            </p>
          </div>
        </div>
      </PopoverContent>
    </Popover>
  );
}

function Row({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div className="flex items-baseline justify-between gap-3">
      <span className="text-text-muted">{label}</span>
      <span className="text-text-primary tabular-nums truncate text-right">{value}</span>
    </div>
  );
}
